import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { useUpdateCustomerMaterialsMutation } from "@saleor/graphql";
import useNotifier from "@saleor/hooks/useNotifier";
import React, { useState } from "react";

import DialogWaitingConfirm from "../DialogWaitingConfirm";
import useStyles from "./styles";

interface DialogConfirmEdit {
  onSuccess: (data) => void;
  onClose: () => void;
  onError?: (errors: {
    materialCode?: boolean;
    materialCodeValue?: boolean;
    soldToMaterialCode?: boolean;
    soldToMaterialCodeValue?: boolean;
  }) => void;
  open: boolean;
  dataItem: any;
}

const DialogConfirmEdit: React.FC<DialogConfirmEdit> = ({
  onClose,
  open,
  dataItem,
  onError,
  onSuccess
}) => {
  const classes = useStyles();
  const [openProcessDialog, setOpenProcessDialog] = useState<boolean>(false);
  const notify = useNotifier();

  const isNewDataAvailable = (newData, oldData) =>
    newData && oldData && newData !== oldData;

  const getDialogMaxWidth = (): "sm" | "md" => {
    const newSoldToMaterialCode =
      dataItem?.newDataItem?.soldToMaterialCode || "";
    const oldSoldToMaterialCode =
      dataItem?.oldDataItem?.soldToMaterialCode || "";

    return newSoldToMaterialCode.length > 20 ||
      oldSoldToMaterialCode.length > 20
      ? "sm"
      : "sm";
  };

  const getDialogClassName = () => {
    const newSoldToMaterialCode =
      dataItem?.newDataItem?.soldToMaterialCode || "";
    const oldSoldToMaterialCode =
      dataItem?.oldDataItem?.soldToMaterialCode || "";

    const isLongText =
      newSoldToMaterialCode.length > 20 || oldSoldToMaterialCode.length > 20;
    return `${classes.root} ${isLongText ? classes.width800 : ""}`;
  };

  const isLongText = () => {
    const newSoldToMaterialCode =
      dataItem?.newDataItem?.soldToMaterialCode || "";
    const oldSoldToMaterialCode =
      dataItem?.oldDataItem?.soldToMaterialCode || "";

    return (
      newSoldToMaterialCode.length > 20 || oldSoldToMaterialCode.length > 20
    );
  };
  const [updateCustomerMaterials] = useUpdateCustomerMaterialsMutation({
    onCompleted: data => {
      if (
        data.updateCustomerMaterials.itemErrors &&
        data.updateCustomerMaterials.itemErrors.some(
          error =>
            error.materialCode === true || error.soldToMaterialCode === true
        )
      ) {
        const isMaterialCodeDuplicate = data.updateCustomerMaterials.itemErrors.some(
          error => error.materialCode === true
        );
        const isSoldToMaterialCodeDuplicate = data.updateCustomerMaterials.itemErrors.some(
          error => error.soldToMaterialCode === true
        );
        const errorInfo = {
          materialCode: isMaterialCodeDuplicate,
          soldToMaterialCode: isSoldToMaterialCodeDuplicate,
          materialCodeValue: dataItem?.newDataItem?.materialCode || "",
          soldToMaterialCodeValue:
            dataItem?.newDataItem?.soldToMaterialCode || ""
        };
        if (onError) {
          onError(errorInfo);
        }
      } else {
        onSuccess({
          id: dataItem?.oldDataItem?.id,
          data: data?.updateCustomerMaterials?.items[0] || null
        });
      }
    },
    onError: error => {
      notify({
        status: "error",
        apiMessage: error.message
      });
    }
  });

  const onSubmitConfirm = async () => {
    if (dataItem && dataItem.newDataItem) {
      setOpenProcessDialog(true);
      await updateCustomerMaterials({
        variables: {
          id: dataItem?.oldDataItem?.id,
          soldToMaterialCode: dataItem?.newDataItem?.soldToMaterialCode,
          materialCode: dataItem?.newDataItem?.materialCode
        }
      });
      setOpenProcessDialog(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        className={getDialogClassName()}
        fullWidth
        maxWidth={getDialogMaxWidth()}
      >
        <DialogTitle className={classes.dialogTitle}>
          <MyTypography variant="h6" className={classes.titleHeader}>
            ยืนยันการแก้ไข
          </MyTypography>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2} className={classes.detailsContainer}>
            <Grid item xs={3} className={classes.titleContainer}>
              <p>Details</p>
            </Grid>
            <Grid
              item
              xs={isLongText() ? 5 : 4}
              className={classes.titleContainer}
            >
              <p>Customer Material</p>
            </Grid>
            <Grid item xs={4} className={classes.titleContainer}>
              <p>Material Code</p>
            </Grid>
            <Grid item xs={3} className={classes.titleRowContainer}>
              <p>ข้อมูลเดิม</p>
            </Grid>
            <Grid
              item
              xs={isLongText() ? 5 : 4}
              className={`${classes.titleColContainer} ${classes.lineLimit}`}
            >
              <p>{dataItem?.oldDataItem?.soldToMaterialCode || ""}</p>
            </Grid>
            <Grid
              item
              xs={4}
              className={`${classes.titleColContainer} ${classes.lineLimit}`}
            >
              <p>{dataItem?.oldDataItem?.materialCode || ""}</p>
            </Grid>
            <Grid item xs={3} className={classes.titleNewContainer}>
              <p>ข้อมูลใหม่</p>
            </Grid>
            <Grid
              item
              xs={isLongText() ? 5 : 4}
              className={`${classes.titleColContainer} ${
                isNewDataAvailable(
                  dataItem?.newDataItem.soldToMaterialCode,
                  dataItem?.oldDataItem?.soldToMaterialCode
                )
                  ? classes.newDataItem
                  : ""
              } ${classes.lineLimit}`}
            >
              <p>{dataItem?.newDataItem?.soldToMaterialCode || ""}</p>
            </Grid>
            <Grid
              item
              xs={4}
              className={`${classes.titleColContainer} ${
                isNewDataAvailable(
                  dataItem?.newDataItem.materialCode,
                  dataItem?.oldDataItem?.materialCode
                )
                  ? classes.newDataItem
                  : ""
              } ${classes.lineLimit}`}
            >
              <p>{dataItem?.newDataItem?.materialCode || ""}</p>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions disableSpacing={true}>
          <Button
            color="primary"
            variant="outlined"
            className={classes.typeButton}
            onClick={onClose}
          >
            <MyTypography variant="body1" className={classes.btnClose}>
              ยกเลิก
            </MyTypography>
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={`${classes.typeButton} ${classes.typeButtonSubmit}`}
            onClick={() => onSubmitConfirm()}
          >
            <MyTypography variant="body1" className={classes.btnSubmit}>
              ยืนยันดำเนินการต่อ
            </MyTypography>
          </Button>
        </DialogActions>
      </Dialog>
      {openProcessDialog && (
        <DialogWaitingConfirm
          open={openProcessDialog}
          onClose={() => {
            setOpenProcessDialog(false);
          }}
        />
      )}
    </>
  );
};

export default DialogConfirmEdit;
